export interface VimeoVideo {
  uri: string;
  name: string;
  description: string;
  duration: number;
  created_time: string;
  pictures?: {
    sizes: Array<{
      width: number;
      height: number;
      link: string;
    }>;
  };
  embed?: {
    html: string;
  };
  player_embed_url: string;
  tags?: Array<{
    uri: string;
    name: string;
    tag: string;
    canonical: string;
  }>;
  files?: Array<{
    quality: string;
    type: string;
    width: number;
    height: number;
    link: string;
    created_time: string;
    fps: number;
    size: number;
    md5: string;
    public_name: string;
    size_short: string;
  }>;
}

export interface VimeoUser {
  uri: string;
  name: string;
  account: string;
}

const VIMEO_API_BASE = '/api/vimeo';

export class VimeoAPI {
  private accessToken: string;

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const response = await fetch(`${VIMEO_API_BASE}/proxy${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Vimeo API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async getCurrentUser(): Promise<VimeoUser> {
    return this.request('/me');
  }

  async getUserVideos(page: number = 1, perPage: number = 25): Promise<{
    data: VimeoVideo[];
    total: number;
    page: number;
    per_page: number;
  }> {
    return this.request(`/me/videos?page=${page}&per_page=${perPage}&fields=uri,name,description,duration,created_time,pictures,player_embed_url,tags,files`);
  }

  async getVideo(videoId: string): Promise<VimeoVideo> {
    return this.request(`/videos/${videoId}?fields=uri,name,description,duration,created_time,pictures,player_embed_url,tags,files`);
  }

  static getVideoIdFromUri(uri: string): string {
    return uri.split('/').pop() || '';
  }

  static getBestThumbnail(video: VimeoVideo): string | undefined {
    if (!video.pictures?.sizes || video.pictures.sizes.length === 0) {
      return undefined;
    }

    // Find the largest thumbnail that's reasonable for our UI (around 320px width)
    const sizes = video.pictures.sizes.sort((a, b) => a.width - b.width);
    const ideal = sizes.find(size => size.width >= 320) || sizes[sizes.length - 1];
    return ideal.link;
  }

  static getBestDirectFileUrl(video: VimeoVideo): string | undefined {
    if (!video.files || video.files.length === 0) {
      return undefined;
    }

    // Sort files by quality/size (higher quality first)
    const sortedFiles = video.files
      .filter(file => file.link && file.type === 'video/mp4') // Only include mp4 files with valid links
      .sort((a, b) => {
        // Sort by quality (hd, sd, mobile) and then by size
        const qualityOrder = { 'hd': 3, 'sd': 2, 'mobile': 1 };
        const aQuality = qualityOrder[a.quality as keyof typeof qualityOrder] || 0;
        const bQuality = qualityOrder[b.quality as keyof typeof qualityOrder] || 0;
        
        if (aQuality !== bQuality) {
          return bQuality - aQuality; // Higher quality first
        }
        
        return b.size - a.size; // Larger size first
      });

    return sortedFiles[0]?.link;
  }
}

export const initializeVimeoOAuth = () => {
  const clientId = import.meta.env.VITE_VIMEO_CLIENT_ID;
  const redirectUri = `${window.location.origin}/auth/vimeo/callback`;
  const scope = 'public private video_files';
  
  const authUrl = `https://vimeo.com/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}`;
  
  window.location.href = authUrl;
};

export const exchangeVimeoCode = async (code: string): Promise<string> => {
  const response = await fetch('/api/vimeo/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    throw new Error('Failed to exchange Vimeo authorization code');
  }

  const data = await response.json();
  return data.access_token;
};
